<template>
  <div :class="appClasses">
    <div
      ref="headerWrapper"
      class="app__header-wrapper"
      :class="{'app__header-wrapper--hide' : hideHeaderWrapper}"
    >
      <UiPromoBannerSmart
        ref="banner"
        class="app__banner"
        @click="(value: string) => sendAcademicaClickBanner({link: value})"
        @visible="sendAcademicaShowBanner"
      />

      <UiHeaderSmart
        ref="header"
        class="app__header"
        :class="{'app__header--hide': hideHeader}"
        @update:search-field-dropdown="isSearchFieldDropdownOpen = $event"
        @visible:banner="sendAcademicaShowMenuBanner"
        @click:banner="(value: string) => sendAcademicaClickMenuBanner({link: value})"
        @visible:navigation="sendAcademicaShowMenu('header', {})"
        @click:search-field="sendAcademicaClickSearch"
        @update:search="sendAcademicaEnteringTextIntoSearch"
        @submit:search="sendAcademicaViewSearchResult"
        @visible:user-menu="sendAcademicaShowBurgerMenu"
        @click:menu-item="(value: string) => sendAcademicaClickBurgerMenu({link: value})"
        @click:direction="(index: number) => {
          sendAcademicaClickMenu('header', {
            selected_direction_id: directionsWithQuery[index]?.slug || '',
          })
        }"
        @click:theme="isThemeModalOpen = true"
      />
    </div>

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <UiFooterSmart
      :have-promo="landing?.rawData.is_promo"
      @visible:navigation="sendAcademicaShowMenu('footer', {})"
      @click:direction="(index) => {
        sendAcademicaClickMenu('footer', {
          selected_direction_id: directionsWithQuery[index]?.slug || '',
        })
      }"
      @visible:contacts="sendAcademicaShowContactBlock"
      @click:contacts="sendAcademicaClickContactBlock"
      @click:social="handleSocialClick"
    />

    <div class="app__navigation">
      <ProjectTabBar
        v-if="isMobile"
        @click:theme="isThemeModalOpen = true"
      />
    </div>
  </div>

  <UiThemeModal
    v-if="isThemeModalOpen"
    @click:theme="handleThemeChangeClick"
    @click:close="isThemeModalOpen = false"
  />

  <div class="app-sprite" v-html="SvgSprite" />
</template>

<script setup lang="ts">
  import type { VNodeRef } from 'vue'
  import { storeToRefs } from 'pinia'

  import UiHeaderSmart from '@academica-box/components/Smarts/UiHeader.smart/UiHeader.smart.vue'
  import UiPromoBannerSmart from '@academica-box/components/Smarts/UiPromoBanner.smart/UiPromoBanner.smart.vue'
  import UiFooterSmart from '@academica-box/components/Smarts/UiFooter.smart/UiFooter.smart.vue'
  import UiThemeModal from '@academica-box/components/Modals/UiThemeModal/UiThemeModal.vue'

  import { useCommonStore } from '@academica-box/composables/store/common'
  import { useDirectionsStore } from '@academica-box/composables/store/directions'
  import { usePromoStore } from '@academica-box/composables/store/promo'
  import { useSubscribesStore } from '@academica-box/composables/store/subscribes'
  import { useUserStore } from '@academica-box/composables/store/user'
  import { useSearchStore } from '@skillbox/academica-box/src/composables/store/search'

  import type { ThemeTypes } from '@academica-box/plugins/theme'

  import { useLandingStore } from '@/store/landing'

  import {
    sendAcademicaShowMenu,
    sendAcademicaClickMenu,
    sendAcademicaShowBurgerMenu,
    sendAcademicaClickBurgerMenu,
    sendAcademicaShowContactBlock,
    sendAcademicaClickContactBlock,
    sendAcademicaClickSocialVk,
    sendAcademicaClickSocialTg,
    sendAcademicaClickSearch,
    sendAcademicaEnteringTextIntoSearch,
    sendAcademicaViewSearchResult,
    sendAcademicaShowBanner,
    sendAcademicaShowMenuBanner,
    sendAcademicaClickMenuBanner,
    sendAcademicaClickBanner
  } from '@/pages/analytics'

  import ProjectTabBar from '@/components/ProjectTabBar.vue'

  import SvgSprite from '@/assets/svg/sprite.svg?raw'

  const route = useRoute()
  const config = useRuntimeConfig()
  const viewport = useViewport()
  const { $theme } = useNuxtApp()

  const directionsStore = useDirectionsStore()
  const { directionsWithQuery } = useDirectionsStore()
  const userStore = useUserStore()
  const promoStore = usePromoStore()
  const { promo } = storeToRefs(promoStore)
  const subscribesStore = useSubscribesStore()
  const { isTrialAvailable, isUserSubsriptionLoaded } = storeToRefs(subscribesStore)
  const searchStore = useSearchStore()
  const commonStore = useCommonStore()
  const landingStore = useLandingStore()
  const { landing } = storeToRefs(landingStore)

  const isSearchFieldDropdownOpen = ref(false)
  const isThemeModalOpen = ref(false)

  const header = ref<VNodeRef | null>(null)
  const hideHeader = ref(false)
  const lastScrollTop = ref(0)
  const banner = ref<VNodeRef | null>(null)
  const headerWrapper = ref<VNodeRef | null>(null)
  const hideHeaderWrapper = ref(false)

  const accessToken = useCookie('access-token')

  const isMobile = computed(() => viewport.isLessThan('lg'))

  const appClasses = computed(() => {
    return [
      'app',
      {
        'app--tilda-page': landing?.value?.rawData.tilda_page_data,
        'app--with-promo-banner': isTrialAvailable || promo.value?.header_banner,
        'app--header-wrapper-hide': hideHeaderWrapper.value,
        'app--header-hide': hideHeader.value,
        'app--header-search-field-open': isSearchFieldDropdownOpen.value
      }
    ]
  })

  const onScrollHeaderHide = () => {
    const HEADER_HEIGHT = 64

    if (isSearchFieldDropdownOpen.value) {
      return
    }

    const scroll = document.documentElement.scrollTop
    let safeGap = 5

    if (isMobile.value && header?.value?.$el.offsetHeight !== HEADER_HEIGHT) {
      safeGap = 118 // 118 - mobile header height with open search field
    }

    if (isMobile.value) {
      if (scroll > lastScrollTop?.value + safeGap) {
        hideHeaderWrapper.value = true
      } else if (isMobile.value && scroll < lastScrollTop?.value - safeGap) {
        hideHeaderWrapper.value = false
      }
    } else if (scroll > lastScrollTop?.value + safeGap) {
      hideHeader.value = true
    } else if (!isMobile.value && scroll < lastScrollTop?.value - safeGap) {
      hideHeader.value = false
    }

    lastScrollTop.value = scroll <= 50 ? 50 : scroll // 50 - safe scroll area at the top of the page
  }

  const handleSocialClick = (value: string) => {
    if (value === 'ВКонтакте' || value === 'VK') {
      sendAcademicaClickSocialVk({})
    }

    if (value === 'Telegram') {
      sendAcademicaClickSocialTg({})
    }
  }

  const handleThemeChangeClick = (value: ThemeTypes) => {
    $theme.postTheme(value)
  }

  onServerPrefetch(async () => {
    if (accessToken.value) {
      await Promise.all([userStore.getUser(), subscribesStore.get()])
    } else {
      isUserSubsriptionLoaded.value = true
    }

    await promoStore.getPromo()

    $theme.getTheme()
  })

  if (accessToken.value) {
    await useAsyncData(
      'getUserInfo',
      () => userStore.getUserInfo()
    )
  }

  // TODO Временное решение, вообще такие вещи скорее всего должны отправляться через gtm
  onBeforeMount(() => {
    const { cl_uid: clUidParam, utm_source: utmSourceParam } = route.query
    const clUid = useCookie<string>('x_cl_uid', {
      maxAge: 90 * 24 * 60 * 60 // 90 дней
    })
    const utmSource = useCookie<string>('x_utm_source', {
      maxAge: 90 * 24 * 60 * 60 // 90 дней
    })

    if (clUidParam) {
      clUid.value = clUidParam as string
    }

    if (utmSourceParam) {
      utmSource.value = utmSourceParam as string
    }
  })

  onMounted(() => {
    window.addEventListener('scroll', onScrollHeaderHide)
  })

  onDeactivated(() => {
    window.removeEventListener('scroll', onScrollHeaderHide)
  })

  useAsyncData(
    'getDirections',
    () => directionsStore.getDirections({ isAcademgen: true })
  )

  useAsyncData(
    'getActiveSubscriptions',
    () => subscribesStore.getActiveSubscriptions()
  )

  useAsyncData(
    'getSuggestionsData',
    () => searchStore.getSuggestionsData()
  )

  await useAsyncData(
    'getFooter',
    () => commonStore.getFooter()
  )

  await useAsyncData(
    'getLanding',
    () => landingStore.getLanding(route.params.course as string)
  )

  useHead({
    link: [
      { rel: 'apple-touch-icon', sizes: '180x180', href: `/favicon/${config.public.enviroment}/apple-touch-icon.png` },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: `/favicon/${config.public.enviroment}/favicon-32x32.png` },
      { rel: 'icon', type: 'image/png', sizes: '16x16', href: `/favicon/${config.public.enviroment}/favicon-16x16.png` },
      { rel: 'icon', href: `/favicon/${config.public.enviroment}/favicon.ico` },
      { rel: 'manifest', href: `/favicon/${config.public.enviroment}/site.webmanifest` },
    ]
  })
</script>

<style lang="scss">
  @use '@academica-box/utils/styles/mixins/UiZIndex' as *;

  .app {
    width: 100vw;
  }

  .app__header-wrapper {
    @include z-index(sticky, header);

    top: 0;
    transition: all .25s ease-in-out;

    &--hide {
      transform: translateY(-100%);
    }
  }

  .app__header {
    transition: all .25s ease-in-out;

    &--hide {
      transform: translateY(-101%);
    }
  }

  .app__navigation {
    @include z-index(fixed, navigation);

    bottom: 0;
    width:100%;
  }

  .app-sprite {
    display: none;
  }

  .app--tilda-page {
    .app__header-wrapper,
    .app__navigation {
        z-index: 100000; // нужен для того, чтобы перекрыть z-index-ы тильды
    }
  }
</style>
